import { inject } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';

import { I18nService } from '@core/i18n-legacy';
import { QuestionWithOptionsService } from '@tellent-ask-hr/dynamic-forms-smart-ui';
import type {
  DefineFormData,
  DynamicFormConfig,
  RespondFormData,
  UnwrapSignal,
} from '@tellent-ask-hr/dynamic-forms-types';
import type { MultiSelectTemplateItem } from '@tellent-ask-hr/templates-types';

export class SelectMultiConfig implements DynamicFormConfig {
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly questionWithOptionsService = inject(QuestionWithOptionsService);
  private readonly i18n = inject(I18nService);

  public readonly kind: MultiSelectTemplateItem['type'] = 'multi_select';
  public readonly defineConfig: DynamicFormConfig['defineConfig'] = {
    name: this.i18n.translate('tlc.ask_hr.form_template_create.configs.multiple_choice'),
    icon: 'done-all',
  };

  public readonly components = {
    define: () =>
      import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.QuestionWithOptionsComponent),
    respond: () =>
      import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.RespondSelectManyComponent),
    present: () =>
      import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.TextPresenterComponent),
  };

  public readonly defineForm = (data?: DefineFormData) =>
    this.questionWithOptionsService.createQuestionForm(data);

  public readonly respondConfig = (data: {
    question: string;
    hint: string;
    required: boolean;
    options: Array<{ value: string }>;
  }): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').RespondSelectManyComponent['respondConfig']
  > => data;

  public readonly respondForm = (
    data: RespondFormData,
  ): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').RespondSelectManyComponent['respondForm']
  > =>
    this.formBuilder.group({
      value: this.formBuilder.control(<string[]>[], {
        validators: data.required ? [Validators.required] : [],
      }),
    });

  public readonly presentConfig = (
    data: MultiSelectTemplateItem,
  ): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').TextPresenterComponent['config']
  > => ({
    heading: data.name,
    value: data.value ? data.value.join(', ') : null,
    kind: {
      type: 'text',
      multiline: false,
    },
  });
}
